import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { meQueryKey } from '../api/me';
import type { Me } from '../api/types';
import { Screen, Standalone } from './Frame';
import { ErrorScreen } from './ErrorScreen';

/**
 * Вход по ссылке-приглашению: человек открыл `?startapp=join_<code>` и ещё не
 * администратор. Приглашение одноразовое, поэтому жмём кнопку осознанно, а не
 * автоматически при загрузке — случайно открытая ссылка не должна его сжигать.
 *
 * Заодно спрашиваем имя и ник: имя станет карточкой специалиста, ник — контактом
 * администратора, который видят клиенты в сообщениях об отменах и переносах.
 */
export function JoinScreen({ me }: { me: Me }) {
  const qc = useQueryClient();
  const [name, setName] = useState(me.pendingJoin?.suggestedName ?? '');
  const [tg, setTg] = useState(me.pendingJoin?.suggestedUsername ?? '');

  const join = useMutation({
    mutationFn: () => api.post<unknown>('/api/join', {
      code: me.pendingJoin?.code,
      name: name.trim(),
      tgUsername: tg.trim().replace(/^@+/, ''),
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: meQueryKey }),
  });

  const ready = name.trim().length > 1;

  if (join.error) return <Standalone title="Приглашение"><ErrorScreen error={join.error} onRetry={() => join.reset()} /></Standalone>;

  return (
    <Standalone title="Приглашение">
      <Screen>
        <div className="hero">
          <div className="avatar">{initials(me.center.title)}</div>
          <div className="hero-txt">
            <h2>{me.center.title}</h2>
            <p>Вас приглашают администратором бота.</p>
          </div>
        </div>

        <div className="field">
          <label htmlFor="joinName">Как вас зовут</label>
          <input
            id="joinName"
            type="text"
            placeholder="Имя и фамилия"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <span className="hint">Так вас увидят коллеги и клиенты в расписании.</span>
        </div>

        <div className="field">
          <label htmlFor="joinTg">Ник в Telegram</label>
          <input
            id="joinTg"
            type="text"
            placeholder="@nickname"
            autoCapitalize="off"
            autoCorrect="off"
            value={tg}
            onChange={(e) => setTg(e.target.value)}
          />
          <span className="hint">Подставляется как {'{админ}'} в сообщениях об отменах и переносах.</span>
        </div>

        <p className="lead">
          После подтверждения откроется панель управления организацией.
        </p>
        <p className="lead">
          Ссылка действует 14 дней и сработает один раз. Если она перестанет действовать,
          попросите пригласившего отправить новую.
        </p>
        <button className="btn solid" disabled={!ready || join.isPending} onClick={() => join.mutate()}>
          {join.isPending ? 'Подтверждаем…' : 'Принять приглашение'}
        </button>
      </Screen>
    </Standalone>
  );
}

function initials(title: string): string {
  return title
    .replace(/[«»"]/g, '')
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w.slice(0, 1).toUpperCase())
    .join('');
}
